/**
 * The tooltip component (M1.1, docs/11-interface.md — "tooltips are
 * infrastructure, not garnish").
 *
 * One shared component that decomposes any number into the lines that compose
 * it, and **nests**: a line inside an explanation is itself a `Figure`, so it
 * can be opened in turn, Paradox-style. The data is a `Figure` tree built
 * sim-side (`campaign::ui::Figure`) and read from the UI store; nothing here
 * computes a value, which is what keeps a tooltip from ever disagreeing with
 * the number it explains. The `?` beside a figure that names a handbook page
 * opens that page — the paragraph-length half of the same job.
 *
 * Interaction: hover shows the first level; clicking a line pins it open and
 * shows *its* lines; clicking the pinned figure closes it. Keyboard: the
 * anchor is focusable and Escape closes. Kept deliberately plain — the
 * legibility audit (M1.8) will judge coverage, not chrome.
 */

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import type { Figure } from '../bridge';
import { openHandbook } from './panels';

export function Tip({
  figure,
  children,
  className,
}: {
  figure: Figure;
  children?: ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const anchor = useRef<HTMLSpanElement>(null);
  const bubble = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!pinned) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPinned(false);
        setOpen(false);
      }
    };
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (anchor.current?.contains(target) || bubble.current?.contains(target)) return;
      setPinned(false);
      setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [pinned]);
  const show = open || pinned;
  return (
    <span
      ref={anchor}
      className={`tip-anchor ${className ?? ''}`}
      tabIndex={0}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => !pinned && setOpen(false)}
      onClick={(e) => {
        e.stopPropagation();
        setPinned((p) => !p);
      }}
    >
      {children ?? <span className="tip-value">{figure.value}</span>}
      {show && (
        <Bubble anchor={anchor} bubble={bubble} pinned={pinned}>
          <FigureNode figure={figure} depth={0} pinned={pinned} />
          {!pinned && (figure.lines?.length ?? 0) > 0 && (
            <div className="tip-hint">click to pin · click a line to open it</div>
          )}
        </Bubble>
      )}
    </span>
  );
}

/**
 * The bubble itself, portalled to the overlay root and positioned in viewport
 * coordinates. Rendering it inside the anchor was clipped by every scrolling
 * panel's `overflow` and drawn under whichever overlay came later in the tree;
 * a portal escapes both, and the position is measured from the anchor's
 * rectangle so it still hangs off the number it explains — flipping above or
 * leftward when the viewport edge is near.
 */
function Bubble({
  anchor,
  bubble,
  pinned,
  children,
}: {
  anchor: RefObject<HTMLSpanElement | null>;
  bubble: RefObject<HTMLDivElement | null>;
  pinned: boolean;
  children: ReactNode;
}) {
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const place = () => {
    const a = anchor.current?.getBoundingClientRect();
    const b = bubble.current?.getBoundingClientRect();
    if (!a || !b) return;
    const margin = 8;
    let left = a.left;
    let top = a.bottom + 4;
    if (left + b.width > window.innerWidth - margin) left = Math.max(margin, window.innerWidth - margin - b.width);
    if (top + b.height > window.innerHeight - margin) {
      const above = a.top - 4 - b.height;
      top = above >= margin ? above : Math.max(margin, window.innerHeight - margin - b.height);
    }
    setPos((p) => (p && p.left === left && p.top === top ? p : { left, top }));
  };
  // Measure after paint, and again whenever the content grows (a line opened)
  // or the page scrolls or resizes under a pinned bubble.
  useLayoutEffect(place);
  useEffect(() => {
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  });
  const root = document.getElementById('overlays') ?? document.body;
  return createPortal(
    <div
      ref={bubble}
      className={`tip ${pinned ? 'pinned' : ''}`}
      role="tooltip"
      style={pos ? { left: pos.left, top: pos.top } : { left: 0, top: 0, visibility: 'hidden' }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    root,
  );
}

/** One figure and, if opened, the lines it is made of. */
function FigureNode({ figure, depth, pinned }: { figure: Figure; depth: number; pinned: boolean }) {
  // The first level is always shown; deeper levels open on click once the
  // tooltip is pinned, so a hover never unfolds a whole tree.
  const [opened, setOpened] = useState(depth === 0);
  const lines = figure.lines ?? [];
  const tone = figure.amount == null ? '' : figure.amount < 0 ? 'neg' : figure.amount > 0 ? 'pos' : '';
  return (
    <div className={`tip-node depth-${Math.min(depth, 4)}`}>
      <div
        className={`tip-line ${lines.length > 0 ? 'has-lines' : ''} ${opened ? 'opened' : ''}`}
        onClick={(e) => {
          if (lines.length === 0) return;
          e.stopPropagation();
          if (pinned || depth === 0) setOpened((o) => !o);
        }}
      >
        <span className="tip-label">
          {lines.length > 0 && <span className="tip-caret">{opened ? '▾' : '▸'}</span>}
          {figure.label}
        </span>
        <span className={`tip-amount ${tone}`}>{figure.value}</span>
        {figure.page && (
          <button
            className="tip-page"
            title="open in the handbook"
            onClick={(e) => {
              e.stopPropagation();
              openHandbook(figure.page!);
            }}
          >
            ?
          </button>
        )}
      </div>
      {figure.note && <div className="tip-note">{figure.note}</div>}
      {opened && lines.length > 0 && (
        <div className="tip-lines">
          {lines.map((line, i) => (
            <FigureNode key={`${line.label}-${i}`} figure={line} depth={depth + 1} pinned={pinned} />
          ))}
        </div>
      )}
    </div>
  );
}

/** A plain figure with no decomposition — for numbers the store marks atomic. */
export function atomic(label: string, value: string, note?: string): Figure {
  return { label, value, note: note ?? null, amount: null, page: null, lines: [] };
}
